// libs
import { useCallback, useMemo, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
// components
import useContents from '@/modules/corporate/hooks/useContents';
import useGroups from '@/modules/corporate/modules/group/views/GroupManager/hooks/useGroups';
import useEvents from '@/modules/events/hooks/useEvents';
// types
import {
  AssignmentItemTypeEnum,
  ContentCollectionGroupType,
  ContentEntity,
  DueDateNestingLevelEnum,
  EventUnion,
  GroupStatusEnum,
  GroupTypeEnum,
  NestedDueDateConfig,
  PathSectionType,
} from '@/types/schema';

import { getSelectedDueDate } from '../../../utils';
import { ContentListProps } from '../types';
import { ALL_CONTENT_TYPES } from '../utils';

export type AdaptedNestedDueDateConfig = Record<string, NestedDueDateConfig> | undefined;

export type InViewItemForDueDate = {
  itemType: AssignmentItemTypeEnum;
  dueDateNestingLevel?: DueDateNestingLevelEnum;
  editingContent?: ContentEntity;
  editingCollection?: ContentCollectionGroupType;
  editingSection?: PathSectionType;
  editingSession?: EventUnion;
  nestedDueDateConfig?: AdaptedNestedDueDateConfig;
  history?: InViewItemForDueDate;
};

export const useContentAndCollectionList = (props: ContentListProps) => {
  const {
    contentIds,
    collectionIds,
    assignmentItemsDueDateConfig,
    setFieldValue,
    eventSelections,
  } = props;
  const [openContentsDialog, { toggle: toggleContentsDialog }] = useToggle(false);
  const [inViewItemForDueDate, setInViewItemForDueDate] = useState<InViewItemForDueDate>();
  const selectedDueDateData = inViewItemForDueDate
    ? getSelectedDueDate(inViewItemForDueDate, assignmentItemsDueDateConfig)
    : undefined;

  const onContentChange = useCallback(
    (value: any) => {
      setFieldValue('contentIds', value, true);
    },
    [setFieldValue],
  );

  const onCollectionChange = useCallback(
    (value: any) => {
      setFieldValue('collectionIds', value, true);
    },
    [setFieldValue],
  );

  const onSessionChange = useCallback(
    (value: any) => {
      setFieldValue('eventSelections', value, true);
    },
    [setFieldValue],
  );

  const setAssignmentItemsDueDate = useCallback(
    (value: any) => {
      setFieldValue('assignmentItemsDueDateConfig', value, true);
    },
    [setFieldValue],
  );

  const [nestedDialogState, setNestedDialogState] = useState<
    InViewItemForDueDate['nestedDueDateConfig']
  >(
    selectedDueDateData?.itemId
      ? assignmentItemsDueDateConfig?.[selectedDueDateData.itemId]?.nestedDueDateConfig || {}
      : {},
  );
  const { contents, isFetching: fetchingContents } = useContents({
    ids: contentIds,
    types: ALL_CONTENT_TYPES,
  });

  const { groups, isFetching: fetchingCollections } = useGroups({
    filters: {
      ids: collectionIds,
      systemGenerated: false,
      status: [GroupStatusEnum.Published],
      types: [GroupTypeEnum.ContentCollection],
    },
    pause: !collectionIds.length,
  });

  const { events: sessions, isFetching: fetchingSessions } = useEvents({
    filters: {
      eventIds: eventSelections.map((selection) => selection.eventId),
    },
    pause: !eventSelections?.length,
  });

  const contentListItems = useMemo(() => {
    if (!contentIds.length || !contents?.length) {
      return [];
    }

    return contents;
  }, [contentIds, contents]);

  const collectionListItems = useMemo(() => {
    if (!collectionIds.length || !groups?.length || fetchingCollections) {
      return [];
    }

    return groups;
  }, [collectionIds, groups, fetchingCollections]);

  const sessionListItems = useMemo(() => {
    if (!eventSelections.length || !sessions?.length || fetchingSessions) {
      return [];
    }

    return sessions;
  }, [eventSelections.length, sessions, fetchingSessions]);

  const onAddContent = useCallback(
    ({ selectedContentIds, selectedCollectionIds, eventSelections }: any) => {
      onContentChange(selectedContentIds);
      onCollectionChange(selectedCollectionIds);
      onSessionChange(eventSelections);
      toggleContentsDialog();
    },
    [onContentChange, onCollectionChange, onSessionChange, toggleContentsDialog],
  );

  const onDeleteContent = useCallback(
    (deletedContentId: any) => {
      onContentChange(contentIds.filter((contentId) => contentId !== deletedContentId));
    },
    [contentIds, onContentChange],
  );

  const onDeleteCollection = useCallback(
    (deletedCollectionId: any) => {
      onCollectionChange(
        collectionIds.filter((collectionId) => collectionId !== deletedCollectionId),
      );
    },
    [collectionIds, onCollectionChange],
  );

  const onDeleteSession = useCallback(
    (deletedSessionId: any) => {
      onSessionChange(
        eventSelections.filter((selection) => selection.eventId !== deletedSessionId),
      );
    },
    [eventSelections, onSessionChange],
  );

  const onSetDueDateForContent = useCallback(
    (content: ContentEntity, dueDateNestingLevel?: DueDateNestingLevelEnum, isNested?: boolean) => {
      setInViewItemForDueDate({
        editingContent: content,
        dueDateNestingLevel,
        itemType: AssignmentItemTypeEnum.Content,
        history: isNested ? inViewItemForDueDate : undefined,
      });
    },
    [inViewItemForDueDate],
  );

  const onSetDueDateForCollection = useCallback(
    (collection: ContentCollectionGroupType, dueDateNestingLevel?: DueDateNestingLevelEnum) => {
      setInViewItemForDueDate({
        editingCollection: collection,
        dueDateNestingLevel,
        itemType: AssignmentItemTypeEnum.Collection,
      });
    },
    [],
  );

  const onSetDueDateForSection = useCallback(
    (
      section: PathSectionType,
      dueDateNestingLevel?: DueDateNestingLevelEnum,
      isNested?: boolean,
    ) => {
      setInViewItemForDueDate({
        editingSection: section,
        dueDateNestingLevel,
        itemType: AssignmentItemTypeEnum.Section,
        history: isNested ? inViewItemForDueDate : undefined,
      });
    },
    [inViewItemForDueDate],
  );

  const onSetDueDateForSession = useCallback((session: EventUnion) => {
    setInViewItemForDueDate({
      editingSession: session,
      itemType: AssignmentItemTypeEnum.Content,
    });
  }, []);

  const onResetDueDate = useCallback(
    (itemId: string) => {
      const newAssignmentDueDates = {
        ...assignmentItemsDueDateConfig,
        [itemId]: undefined,
      };
      setAssignmentItemsDueDate(newAssignmentDueDates);
    },
    [assignmentItemsDueDateConfig, setAssignmentItemsDueDate],
  );

  const onCloseDueDateDialog = useCallback(() => {
    setInViewItemForDueDate(inViewItemForDueDate?.history);
  }, [inViewItemForDueDate?.history]);

  return {
    state: {
      groups,
      contents,
      contentListItems,
      openContentsDialog,
      collectionListItems,
      inViewItemForDueDate,
      assignmentItemsDueDateConfig,
      nestedDialogState,
      sessions,
      sessionListItems,
    },
    methods: {
      onAddContent,
      onResetDueDate,
      onDeleteContent,
      onDeleteCollection,
      onCloseDueDateDialog,
      toggleContentsDialog,
      onSetDueDateForContent,
      onSetDueDateForSection,
      onSetDueDateForCollection,
      onSetDueDateForSession,
      setAssignmentItemsDueDate,
      setNestedDialogState,
      onDeleteSession,
    },
  };
};
